import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { graphql, readFragment } from '~publish/graphql'

const CollectionListCollection = graphql(/* GraphQL */ `
  fragment CollectionListCollection on FeedCollection {
    id
    name
    feeds {
      id
      name
      lastRefreshed
      connectionMetadata {
        ... on RSSFeedConnectionMetadata {
          __typename
          url
        }
        ... on NewsSearchConnectionMetadata {
          __typename
          query
        }
      }
    }
  }
`)

export const GetCollectionListDetails = graphql(
  /* GraphQL */ `
    query GetCollectionListDetails($feedCollectionId: FeedCollectionId!) {
      feedCollection(input: { id: $feedCollectionId }) {
        ...CollectionListCollection
      }
    }
  `,
  [CollectionListCollection],
)

type UseCollectionListDetailsInput = {
  feedCollectionId: string | undefined
}

type UseCollectionListDetailsResult = {
  loading: boolean
  error?: ErrorLike
  collection:
    | {
        id: string
        name: string
        feeds: Array<{
          id: string
          name: string
          lastRefreshed: string
          connectionMetadata:
            | {
                __typename: 'RSSFeedConnectionMetadata'
                url: string
              }
            | {
                __typename: 'NewsSearchConnectionMetadata'
                query: string | null
              }
        }>
      }
    | undefined
}

export const useCollectionListDetails = (
  input: UseCollectionListDetailsInput,
): UseCollectionListDetailsResult => {
  const { feedCollectionId } = input

  const { data, loading, error } = useQuery(GetCollectionListDetails, {
    variables: {
      feedCollectionId: feedCollectionId ?? '',
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !feedCollectionId,
  })

  const collection = data?.feedCollection
    ? readFragment(CollectionListCollection, data.feedCollection)
    : undefined

  return {
    loading,
    error,
    collection,
  }
}
